<docs>
Single-field text input for Zone forms. Renders one of three shapes via `type`: text (default),
number, or multiline (a deliberately bare `<textarea>`), with an optional label row (label, required
asterisk, info tooltip) above the field via ZoneLabel. Pick the right component: search is always
ZoneSearchInput (there is no `type="search"` here any more; passing it warns and renders a plain
text field); rich multi-line (char counter, helper/warning text, resize, size presets, clearable) is
ZoneTextArea; dropdown ZoneSelect; phone number ZonePhoneField; boolean ZoneCheckbox.

## v-model

The emitted value is always a String, even for `type="number"` (it emits `event.target.value`).
Coerce with `Number(value)` if you need a real number.

## States

Matched to ZoneSelect so the two never read as different states in one form.

- `readonly`: gray-50 fill, gray-300 border, no focus treatment. The native `readonly` attribute, so
  the value is still focusable, selectable and submitted. ZoneSelect reaches the same look by
  disabling its trigger, because a `<button>` has no readonly of its own. Wins over `disabled` if
  both are set.
- `disabled`: the whole field at 30% opacity, label and `#notice` included, plus the native
  `disabled` attribute. No fill of its own: the fade is the state, exactly as in ZoneSelect.
- `invalid`: red border and `aria-invalid` on the field.
- `required`: asterisk in the label row and the native `required` attribute. Figma gives this its
  own cell in the text-input grid; ZoneSelect has no such state.

## Notes

- `name` is set on the native input, so it submits directly in a Rails form (no hidden input).
- `inheritAttrs: false`: any extra attr or DOM event you pass (autocomplete, inputmode, `@blur`,
  `@keydown`…) is forwarded onto the `<input>`/`<textarea>`, not the wrapper.
- Type-scoped props: `min`/`max`/`step` are number only, `rows`/`cols` multiline only; ignored on
  other types.
- The label is associated with the field: `id` (or an auto-generated one) is wired to the label's
  `for`, so clicking the label focuses the input.
- Tooltip content renders through ZoneTooltip and is teleported outside `.w-tw` (known Zone portal
  issue); it may appear unstyled.

Figma: [Text input](https://www.figma.com/design/eOrg3dHaQFZ7VdCZCcomNC/-Zone-DS--Components-Specs?node-id=3854-1627&m=dev)
</docs>

<template>
  <div :class="wrapperClasses">
    <!--
      Label row. #label REPLACES the standard ZoneLabel rather than rendering inside it, so
      callers can compose their own row (e.g. a <ZoneLabel> next to a link) without ending up
      with a <label> nested in a <label>. Slot props carry everything ZoneLabel needs.
    -->
    <slot
      v-if="$slots.label"
      name="label"
      :html-for="resolvedId"
      :required="required"
      :invalid="invalid"
      :tooltip="tooltip"
    />
    <ZoneLabel
      v-else-if="label || tooltip"
      :label="label"
      :required="required"
      :tooltip="tooltip"
      :icon="Boolean(tooltip)"
      :html-for="resolvedId"
    />

    <div class="relative">
      <textarea
        v-if="type === 'multiline'"
        :id="resolvedId"
        :name="name"
        :placeholder="placeholder"
        :disabled="disabled && !readonly"
        :readonly="readonly"
        :required="required"
        :minlength="minlength"
        :maxlength="maxlength"
        :rows="rows"
        :cols="cols"
        :class="inputClasses"
        :value="internalValue"
        :aria-invalid="invalid || undefined"
        v-bind="$attrs"
        @input="onInput"
      />
      <input
        v-else
        :id="resolvedId"
        ref="inputEl"
        :name="name"
        :type="nativeType"
        :placeholder="placeholder"
        :disabled="disabled && !readonly"
        :readonly="readonly"
        :required="required"
        :minlength="minlength"
        :maxlength="maxlength"
        :min="type === 'number' ? min : undefined"
        :max="type === 'number' ? max : undefined"
        :step="type === 'number' ? step : undefined"
        :class="inputClasses"
        :value="internalValue"
        :aria-invalid="invalid || undefined"
        v-bind="$attrs"
        @input="onInput"
      />
    </div>

    <slot name="notice" />
  </div>
</template>

<script lang="ts">
import { computed } from 'vue';
import ZoneLabel from './Label.vue';

// Per-instance fallback id, so the visible label can be `for`-associated with the field
// even when the caller doesn't pass an explicit `id`.
let uid = 0;
</script>

<script setup lang="ts">
defineOptions({ name: 'ZoneTextInput', inheritAttrs: false });

const props = defineProps({
  /** Always emitted back as a String, even with `type="number"`. */
  modelValue: {
    type: [String, Number],
    default: null,
  },
  /** Set on the native input: submits directly in a Rails form. */
  name: {
    type: String,
    default: null,
  },
  /** Wired to the label's `for`; falls back to a generated id. */
  id: {
    type: String,
    default: null,
  },
  /**
   * `multiline` renders a `<textarea>`. There is deliberately no `search`: ZoneSearchInput owns
   * that field.
   */
  type: {
    type: String,
    default: 'text',
    validator: (v: string) => ['text', 'number', 'multiline'].includes(v),
  },
  /** Label row text (the `#label` slot replaces the row). */
  label: {
    type: String,
    default: null,
  },
  /** Info-tooltip text on the label row. */
  tooltip: {
    type: String,
    default: null,
  },
  /** Native placeholder. */
  placeholder: {
    type: String,
    default: null,
  },
  /** The whole field at 30% opacity plus the native `disabled` attribute. */
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Figma's read-only state: the value is shown, greyed and uneditable, but still focusable,
   * selectable and submitted. Wins over `disabled`.
   */
  readonly: {
    type: Boolean,
    default: false,
  },
  /** Asterisk in the label row plus the native `required` attribute. */
  required: {
    type: Boolean,
    default: false,
  },
  /** Red border and `aria-invalid` on the field. */
  invalid: {
    type: Boolean,
    default: false,
  },
  /** Native `minlength`; null leaves the attribute off. */
  minlength: {
    type: Number,
    default: null,
  },
  /** Native `maxlength`; null leaves the attribute off. */
  maxlength: {
    type: Number,
    default: null,
  },
  /**
   * `type="number"` only. Null leaves the attribute off the element entirely: a default range once
   * put `min`/`max` on every number field, so a negative value or anything past a million failed
   * native validation for no reason the caller could see.
   */
  min: {
    // type="number" only
    type: Number,
    default: null,
  },
  /** `type="number"` only; null leaves the attribute off. */
  max: {
    // type="number" only
    type: Number,
    default: null,
  },
  /** `type="number"` only; null leaves the attribute off. */
  step: {
    // type="number" only
    type: Number,
    default: null,
  },
  /** `type="multiline"` only. */
  rows: {
    // type="multiline" only
    type: Number,
    default: 10,
  },
  /** `type="multiline"` only. */
  cols: {
    // type="multiline" only
    type: Number,
    default: 10,
  },
});

const emit = defineEmits<{
  /** On input. Always a String. */
  'update:modelValue': [value: string];
}>();

defineSlots<{
  /**
   * Replaces the whole label row; it does not render inside the built-in ZoneLabel, so render your own
   * ZoneLabel in it (forwarding these props) to keep the standard look. The `label`/`tooltip` props are
   * not rendered meanwhile.
   */
  label?(props: { htmlFor: string; required: boolean; invalid: boolean; tooltip: string | null }): any;
  /** Free-form content below the field, for error/helper text. Unstyled: you own the markup. */
  notice?(): any;
}>();

const generatedId = `zone-text-input-${(uid += 1)}`;

const internalValue = computed(() => props.modelValue);

// Explicit `id` if given, otherwise a stable per-instance id, so the label's
// `for` and the field's `id` always match.
const resolvedId = computed<string>(() => props.id || generatedId);

const nativeType = computed<'number' | 'text'>(() => (props.type === 'number' ? 'number' : 'text'));

const wrapperClasses = computed<string>(() => {
  const classes = ['flex flex-col gap-2 m-0'];

  // `readonly` first, matching `inputClasses` and the STATES block: it wins over `disabled`
  // when both are set, so the field must not also carry the disabled fade. Reversed, the
  // three surfaces of that rule disagreed — the control looked read-only while the wrapper
  // faded it and the native `disabled` attribute made the value unfocusable and unsubmitted.
  if (props.readonly) {
    classes.push('hover:cursor-default');
  } else if (props.disabled) {
    // ZoneSelect fades the WHOLE field at 30% — label and notice included, not just the
    // control — so a disabled text field has to do the same or the two read as different
    // states side by side in one form.
    classes.push('opacity-30 hover:cursor-not-allowed');
  } else {
    classes.push('hover:cursor-pointer');
  }

  return classes.join(' ');
});

const inputClasses = computed<string>(() => {
  // placeholder:font-regular pins the placeholder to 400. The scoped preflight gives
  // `font-weight: inherit` to every input/textarea/select under `.w-tw`, so the field —
  // and with it ::placeholder — takes the weight of whatever ancestor is in effect; drop
  // this and any `font-medium` wrapper silently bolds the placeholder.
  const base = ['w-full text-gray-700 placeholder:font-regular placeholder:text-gray-400'];

  // Line height and corner radius are chosen HERE, exactly once, instead of sitting on `base`
  // for the multiline branch to override. Two utilities of EQUAL specificity are resolved by
  // STYLESHEET order, never by the order they appear in the class attribute — and Tailwind
  // emits `leading-xs` after `leading-5`, and `rounded-md` after `rounded-lg`. An appended
  // override therefore LOSES both, which is exactly what used to happen: multiline rendered
  // at the single-line 1.6rem/0.6rem however it was spelled.
  // The multiline pair (2rem leading, 0.8rem radius) is what ZoneTextArea uses for the very
  // same control.
  base.push(props.type === 'multiline' ? 'leading-5 rounded-lg py-3 px-4' : 'leading-xs rounded-md');

  // Read-only and disabled are ZoneSelect's two states, matched class for class: read-only
  // is a gray-50 fill on a normal gray-300 border, and disabled is the ordinary field under
  // the wrapper's 30% fade rather than a fill of its own. `readonly` wins if both are set,
  // which is also how ZoneSelect's trigger resolves the pair.
  if (props.readonly) {
    base.push('bg-gray-50 border-gray-300 hover:cursor-default focus:border-gray-300 focus:ring-0');
  } else if (props.disabled) {
    base.push('hover:cursor-not-allowed');
  }

  if (props.invalid) {base.push('border-red-500 focus:ring-red-500 focus:border-red-500');}
  if (props.type === 'number') {base.push('[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none');}

  return base.join(' ');
});

function onInput(event: Event) {
  emit('update:modelValue', (event.target as HTMLInputElement | HTMLTextAreaElement).value);
}
</script>
